import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, MapPin, Search, Star, Tag } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const FALLBACK_IMG = "https://images.pexels.com/photos/30003229/pexels-photo-30003229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AREA_IMAGES = [
  "https://images.unsplash.com/photo-1677126369481-81b4ddcf0e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjaXR5JTIwbmVpZ2hib3Job29kJTIwc3RyZWV0fGVufDB8fHx8MTc4NzkzNDQ5Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1627927518258-b67557570840?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjaXR5JTIwbmVpZ2hib3Job29kJTIwc3RyZWV0fGVufDB8fHx8MTc4NzkzNDQ5Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1641465537505-65fb9572571d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtYW5jaGVzdGVyJTIwY2l0eSUyMHN0cmVldCUyMGV2ZW5pbmd8ZW58MHx8fDE3ODc5MzQ4ODR8MA&ixlib=rb-4.1.0&q=85",
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

function RestaurantCard({ r, i }) {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 6) * 0.08 }}>
      <Link to={`/restaurants/${r.slug || r.id}`} data-testid={`restaurant-card-${r.id}`} className="group block">
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card hover:-translate-y-[3px] transition-transform duration-300">
          <img src={r.image_url || FALLBACK_IMG}
            alt={r.name} className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase text-primary">{r.cuisine || "Restaurant"}</p>
              {r.review_count > 0 && (
                <span className="flex items-center gap-1 text-xs text-amber-300">
                  <Star className="w-3.5 h-3.5 fill-amber-300" /> {r.avg_rating} ({r.review_count})
                </span>
              )}
            </div>
            <h3 className="font-display text-xl mt-1">{r.name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
              <MapPin className="w-3.5 h-3.5" /> {r.address}{r.city ? `, ${r.city}` : ""}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Restaurants() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [slugCity, setSlugCity] = useState("");
  const [cuisines, setCuisines] = useState([]);
  const [offers, setOffers] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [cityDraft, setCityDraft] = useState(searchParams.get("city") || "");
  const [cuisine, setCuisine] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (c = cuisine, cityOverride, qOverride) => {
    setLoading(true);
    try {
      const cityVal = cityOverride !== undefined ? cityOverride : city;
      const qVal = qOverride !== undefined ? qOverride : q;
      const params = {};
      if (qVal) params.q = qVal;
      if (cityVal) params.city = cityVal;
      if (c) params.cuisine = c;
      const { data } = await api.get("/restaurants", { params });
      setItems(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    const q0 = searchParams.get("q") || "";
    const c0 = searchParams.get("city") || "";
    load("", c0, q0);
    api.get("/restaurants")
      .then((r) => {
        setAllItems(r.data);
        setCuisines([...new Set(r.data.map((x) => x.cuisine).filter(Boolean))]);
      })
      .catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data.filter((l) => l.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) return;
    api.get("/locations").then((r) => {
      const loc = r.data.find((l) => l.slug === slug);
      if (loc) {
        setSlugCity(loc.name);
        setCity(loc.name);
        setCityDraft(loc.name);
        load("", loc.name);
      }
    }).catch(() => {});
  }, [slug]);

  const pickCuisine = (c) => {
    setCuisine(c);
    load(c);
  };

  const pickCity = (name) => {
    setCity(name);
    setCityDraft(name);
    setSlugCity("");
    load(cuisine, name);
  };

  const submitSearch = () => {
    setCity(cityDraft.trim());
    setSlugCity("");
    load(cuisine, cityDraft.trim());
  };

  const isFiltered = Boolean(city || q || cuisine);
  const counts = useMemo(() => {
    const m = {};
    for (const r of allItems) {
      const k = (r.city || "").toLowerCase();
      if (k) m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [allItems]);

  const groups = useMemo(() => {
    if (isFiltered) return [];
    const byCity = {};
    for (const r of items) {
      const k = r.city || "Other areas";
      (byCity[k] = byCity[k] || []).push(r);
    }
    const ordered = [];
    for (const l of locations) {
      if (byCity[l.name]) ordered.push([l.name, byCity[l.name], l.slug]);
      delete byCity[l.name];
    }
    for (const [k, v] of Object.entries(byCity)) ordered.push([k, v, null]);
    return ordered;
  }, [items, isFiltered, locations]);

  return (
    <div data-testid="restaurants-page" className="max-w-7xl mx-auto px-6 py-16 min-h-screen">
      <p className="text-xs tracking-[0.3em] uppercase text-primary mb-3">Marketplace</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
        {slugCity ? `Restaurants in ${slugCity}` : "Find your table"}
      </h1>
      <p className="mt-4 text-muted-foreground max-w-xl">
        {slugCity ? "Every bookable table in the neighbourhood." : "Pick an area to explore its dining rooms — or search the whole marketplace."}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <Input data-testid="search-name-input" placeholder="Search restaurants..." value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          className="bg-card border-border/60" />
        <Input data-testid="search-city-input" placeholder="Area or city" value={cityDraft}
          onChange={(e) => setCityDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitSearch()}
          className="bg-card border-border/60 sm:max-w-[180px]" />
        <Button data-testid="search-btn" onClick={submitSearch} className="rounded-full px-6">
          <Search className="w-4 h-4 mr-2" /> Search
        </Button>
      </div>

      {locations.length > 0 && (
        <div data-testid="city-chips" className="mt-12">
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-5">Browse by area</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button data-testid="city-card-all" onClick={() => pickCity("")}
              className={`relative overflow-hidden rounded-2xl border aspect-[4/3] text-left transition-transform duration-300 hover:-translate-y-[3px] ${!city ? "border-primary ring-1 ring-primary" : "border-border/60"}`}>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Compass className={`w-10 h-10 ${!city ? "text-primary" : "text-primary/50"}`} />
              </div>
              <div className="absolute bottom-0 p-4">
                <h3 className="font-display text-lg">All areas</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{allItems.length} restaurant{allItems.length === 1 ? "" : "s"}</p>
              </div>
            </button>
            {locations.map((l, i) => (
              <button key={l.id} data-testid={`city-card-${l.slug}`} onClick={() => pickCity(city === l.name ? "" : l.name)}
                className={`group relative overflow-hidden rounded-2xl border aspect-[4/3] text-left transition-transform duration-300 hover:-translate-y-[3px] ${city === l.name ? "border-primary ring-1 ring-primary" : "border-border/60"}`}>
                <img src={AREA_IMAGES[i % AREA_IMAGES.length]} alt={l.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 p-4">
                  <h3 className="font-display text-lg text-white">{l.name}</h3>
                  <p className="text-xs text-white/70 mt-0.5">{counts[l.name.toLowerCase()] || 0} restaurant{(counts[l.name.toLowerCase()] || 0) === 1 ? "" : "s"}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {cuisines.length > 0 && (
        <div data-testid="cuisine-chips" className="mt-10 flex flex-wrap gap-2">
          <button data-testid="cuisine-chip-all" onClick={() => pickCuisine("")}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
              cuisine === "" ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"
            }`}>
            All
          </button>
          {cuisines.map((c) => (
            <button key={c} data-testid={`cuisine-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => pickCuisine(c)}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
                cuisine === c ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"
              }`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {offers.length > 0 && (
        <div className="mt-14" data-testid="offers-section">
          <h2 className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" /> Special offers
          </h2>
          <div className="mt-6 flex gap-4 overflow-x-auto pb-3 -mx-6 px-6">
            {offers.map((o) => (
              <Link key={o.id} to={`/restaurants/${o.restaurant_slug || o.restaurant_id}`} data-testid={`offer-card-${o.id}`}
                className="min-w-[280px] rounded-2xl border border-primary/30 bg-primary/5 p-5 hover:-translate-y-[2px] transition-transform duration-300 block shrink-0">
                <p className="text-xs tracking-[0.2em] uppercase text-primary">{o.restaurant_name}</p>
                <h3 className="font-outfit font-medium text-lg mt-1.5">{o.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{o.description}</p>
                {o.valid_until && <p className="text-xs text-muted-foreground mt-3">Valid until {o.valid_until}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {isFiltered ? (
        <div className="mt-14">
          {city && (
            <div className="flex items-center gap-3 mb-8">
              <p className="text-sm text-muted-foreground">Showing tables in <span className="text-foreground font-medium">{city}</span></p>
              <button data-testid="clear-city-btn" onClick={() => pickCity("")}
                className="text-xs text-primary hover:underline">Clear area</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {items.map((r, i) => <RestaurantCard key={r.id} r={r} i={i} />)}
          </div>
        </div>
      ) : (
        <div className="mt-16 space-y-20">
          {groups.map(([cityName, rests, locSlug]) => (
            <section key={cityName} data-testid={`area-section-${cityName.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-end justify-between gap-4 mb-8">
                <div>
                  <p className="text-xs tracking-[0.3em] uppercase text-primary mb-2">{rests.length} restaurant{rests.length === 1 ? "" : "s"}</p>
                  <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{cityName}</h2>
                </div>
                {(rests.length > 6 || locSlug) && (
                  <Link to={locSlug ? `/c/${locSlug}` : "#"} data-testid={`area-view-all-${(locSlug || cityName).toLowerCase()}`}
                    onClick={!locSlug ? (e) => { e.preventDefault(); pickCity(cityName); } : undefined}
                    className="group flex items-center gap-1.5 text-sm text-primary shrink-0">
                    View all <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {rests.slice(0, 6).map((r, i) => <RestaurantCard key={r.id} r={r} i={i} />)}
              </div>
            </section>
          ))}
        </div>
      )}
      {!loading && items.length === 0 && (
        <p data-testid="no-restaurants-msg" className="mt-16 text-muted-foreground">No restaurants match your search yet.</p>
      )}
    </div>
  );
}
